import { Article, FAQPage, ItemList, Organization, Thing, WithContext } from 'schema-dts';

import { NewsArticle } from '@/resources/NewsArticle';

type NewsArticleStructuredDataBuilder = (article: NewsArticle) => Array<WithContext<Thing>>;

const UNDERLINE_URL = process.env.NEXT_PUBLIC_BASE_URL;

/** Not available from the API — the same publisher for every article. */
const UNDERLINE_PUBLISHER: Organization = {
  '@type': 'Organization',
  name: 'Underline Science',
  url: UNDERLINE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${UNDERLINE_URL}favicon/android-chrome-512x512.png`,
  },
};

/**
 * The ranking in article 117. Fully hardcoded: the article body is markdown in
 * a single `content` field, so there is nothing structured to read it from.
 * Keep in sync by hand whenever the article's list changes.
 */
const BEST_HYBRID_CONFERENCE_PLATFORMS_LIST: WithContext<ItemList> = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Best Hybrid Conference Platforms for Academic and Scientific Events',
  description:
    'Comparison of the top hybrid event platforms for academic conferences, ranked by suitability for scientific societies and academic institutions.',
  numberOfItems: 5,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Underline Science',
      url: 'https://underline.io',
      description:
        'Purpose-built hybrid conference platform for scientific societies and academic institutions. Features DOI assignment, AI-powered search, permanent video archives, and peer review workflows.',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Cvent',
      url: 'https://www.cvent.com',
      description:
        'Enterprise event management platform for large-scale professional associations and corporate events.',
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Whova',
      url: 'https://whova.com',
      description:
        'Hybrid event platform strong on attendee networking and real-time engagement for academic conferences.',
    },
    {
      '@type': 'ListItem',
      position: 4,
      name: 'Bizzabo',
      url: 'https://www.bizzabo.com',
      description:
        'Data-driven hybrid event platform for commercial events with sponsor ROI tracking and CRM integrations.',
    },
    {
      '@type': 'ListItem',
      position: 5,
      name: 'Dryfta',
      url: 'https://dryfta.com',
      description: 'Research and academic conference platform with abstract submission and peer review workflows.',
    },
  ],
};

/**
 * The FAQ block for article 117. Fully hardcoded — these questions and answers
 * exist only here, not in the `news_articles` resource.
 */
const BEST_HYBRID_CONFERENCE_PLATFORMS_FAQ: WithContext<FAQPage> = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What hybrid conference platform is best for scientific societies?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Underline Science is the strongest option for scientific societies. It combines hybrid event management with a permanent video library, DOI assignment for every lecture, AI-powered search, and downloadable papers and posters -- features designed for academic content, not commercial events.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do hybrid conference platforms support abstract submission and peer review?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Most general-purpose platforms (Cvent, Whova, Bizzabo) do not include peer review workflows. Dryfta and Underline Science both support abstract submission and peer review management as part of their academic-focused feature sets.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do academic conferences make their video content citeable after the event?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Underline Science assigns a DOI number to every conference lecture, making it directly citeable in academic papers and literature databases. This is a feature unique to Underline among hybrid event platforms -- general platforms like Cvent and Whova do not assign DOIs.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the best hybrid event platform for conferences in New York?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Underline Science is headquartered in New York and serves major scientific societies worldwide, including ACL, NeurIPS, CVPR, and EMNLP. For academic conferences based in or running events through New York, Underline provides both event management and long-term archiving.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can AI models search conference video content?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Underline Science offers AI-powered search that works across audio, video, papers, posters, slides, and transcripts simultaneously. This lets researchers find specific results or discussions within talks without watching full recordings. General-purpose event platforms do not offer this capability.',
      },
    },
  ],
};

const buildNewsArticleSchema = (article: NewsArticle): WithContext<Article> => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.summary,
  image: article.largeFeaturedImageUrl,
  datePublished: article.publishedOn,
  // The resource exposes no modified timestamp, so this tracks publication.
  dateModified: article.publishedOn,
  author: {
    '@type': 'Person',
    name: article.author,
    url: UNDERLINE_URL,
  },
  publisher: UNDERLINE_PUBLISHER,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${UNDERLINE_URL}news/${article.linkSlug}`,
  },
});

/**
 * Hand-written schema.org markup for individual news articles, keyed by article
 * id. Each entry returns every JSON-LD node that article should emit, one
 * `<script>` per node. Articles absent from this map render no JSON-LD, so
 * adding SEO markup to one article never affects the others.
 *
 * `Article` fields are read off the resource wherever it exposes them, so the
 * markup follows dashboard edits. The publisher block, the author URL and
 * `dateModified` have no API source, and neither do the `ItemList` and
 * `FAQPage` nodes.
 */
export const newsArticleStructuredDataBuilders: Record<string, NewsArticleStructuredDataBuilder> = {
  '117': (article) => [
    buildNewsArticleSchema(article),
    BEST_HYBRID_CONFERENCE_PLATFORMS_LIST,
    BEST_HYBRID_CONFERENCE_PLATFORMS_FAQ,
  ],
};
